// src/utils/saveProgress.js
// ZACHOWUJE DOTYCHCZASOWY INTERFEJS:
//   export function saveLessonResult(userEmail, lessonId, score, maxScore)
//
// Dodatkowo: wrzuca wpis do kolejki synchronizacji z serwerem.
// Kolejka jest bezpieczna offline i wysyła się dopiero po podpięciu authorizedFetch
// z ProgressSyncInit (patrz: src/progress/ProgressSyncInit.jsx).

const LS_KEY = "userProgress";      // zgodnie z obecną strukturą (StudentDashboard to czyta)
const Q_KEY  = "progress_queue_v1"; // kolejka do sync

let getAuthFetch = () => null; // wstrzykiwane przez ProgressSyncInit
export function __initProgressSync(getAuthorizedFetchFn) {
  if (typeof getAuthorizedFetchFn === "function") {
    getAuthFetch = getAuthorizedFetchFn;
  }
}

function readJSON(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; }
}
function writeJSON(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

// ---- KOLEJKA SYNC ----
function readQ() { return readJSON(Q_KEY, []); }
function writeQ(q) { writeJSON(Q_KEY, q); }

// łączenie wpisów po lessonId (taskId) – bierzemy max score, sumujemy czas, itd.
function compactQueue(items) {
  const map = new Map();
  for (const it of items) {
    if (!it?.lessonId) continue;
    const prev = map.get(it.lessonId) || {};
    map.set(it.lessonId, {
      lessonId: it.lessonId,
      userEmail: it.userEmail || prev.userEmail,
      score: Math.max(prev.score ?? 0, it.score ?? 0),
      maxScore: Math.max(prev.maxScore ?? 0, it.maxScore ?? 0),
      timestamp: it.timestamp || prev.timestamp || new Date().toISOString(),
    });
  }
  return [...map.values()];
}

let inflight = false;
let t = null;
function scheduleFlush() {
  if (t) clearTimeout(t);
  t = setTimeout(flushQueue, 1500); // debounce
}

export async function flushQueue() {
  if (inflight) return;
  const authorizedFetch = getAuthFetch && getAuthFetch();
  if (!authorizedFetch) return; // nie podłączono jeszcze auth – nic nie rób

  const raw = readQ();
  if (!raw.length) return;

  inflight = true;
  try {
    const items = compactQueue(raw).map((r) => ({
      // Backend może mapować lessonId -> taskId/topicId po swojej stronie.
      // Dla prostoty wysyłamy to co już mamy lokalnie.
      taskId: r.lessonId,
      status: typeof r.score === "number" && typeof r.maxScore === "number" && r.maxScore > 0
        ? (r.score >= r.maxScore ? "solved" : "started")
        : "started",
      score: (typeof r.score === "number" && typeof r.maxScore === "number" && r.maxScore > 0)
        ? Math.max(0, Math.min(1, r.score / r.maxScore))
        : undefined,
      // Możesz dodać topicId jeśli masz mapowanie po lessonId (opcjonalnie)
      // topicId: mapLessonToTopic(r.lessonId),
      lastAnswer: "", // na razie nie zbieramy – zostawiamy puste
      // meta:
      localTimestamp: r.timestamp,
    }));

    const res = await authorizedFetch("/api/progress/bulk-upsert", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items }),
    });

    if (res.ok) {
      writeQ([]); // czyszczenie kolejki po sukcesie
    }
  } catch {
    // offline / błąd serwera – kolejka zostaje i spróbuje ponownie
  } finally {
    inflight = false;
  }
}

window.addEventListener("online", scheduleFlush);
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") scheduleFlush();
});

// ---- ORYGINALNA FUNKCJA (API BEZ ZMIAN) ----
export function saveLessonResult(userEmail, lessonId, score, maxScore) {
  // 1) zapis lokalny (oryginalne zachowanie)
  const data = readJSON(LS_KEY, {});
  if (!data[userEmail]) data[userEmail] = {};
  data[userEmail][lessonId] = {
    score,
    maxScore,
    timestamp: new Date().toISOString(),
  };
  writeJSON(LS_KEY, data);

  // 2) do kolejki sync
  const q = readQ();
  q.push({ userEmail, lessonId, score, maxScore, timestamp: new Date().toISOString() });
  writeQ(q);
  scheduleFlush();
}
